import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { App, ResizeEvent, ZoomEvent, DragEvent, PointerEvent, Cursor, Text } from 'leafer-ui';
import { EditorMoveEvent } from '@leafer-in/editor';
import debounce from 'lodash/debounce';
import { addListener, removeListener } from 'resize-detector';
import rotatePng from '@assets/rotate.png';
import pencilPng from '@assets/pencil.png';
import stores from '@stores';
import FrameBox from './layers/FrameBox';
import Screenshot from './layers/Screenshot';
import Watermark from './layers/Watermark';
import ShapeLine from './layers/ShapeLine';
import { ScrollBar } from '@leafer-in/scroll'
import { nanoid } from '@utils/utils';
import HotKeys from './HotKeys';
import '@leafer-in/view';
import '@leafer-in/export';

Cursor.set('pencil', { url: pencilPng });

export default observer(({target}) => {
    const appRef = useRef(null);
    
    useEffect(() => {
        // 添加ID到target元素以便更容易找到
        if (target && !target.id) {
            target.id = "editor-canvas-container";
        }
        
        console.log("初始化编辑器视图");
        
        // 确保DOM元素初始化完成
        if (!target) {
            console.error("目标容器不存在，无法初始化编辑器");
            return;
        }
        
        const app = new App({
            view: target,
            editor: {
                lockRatio: 'corner',
                stroke: '#3f99f7',
                skewable: false,
                hover: false,
                middlePoint: { cornerRadius: 100, width: 20, height: 6 },
                rotatePoint: {
                    width: 20,
                    height: 20,
                    fill: {
                        type: 'image',
                        url: rotatePng,
                    },
                },
            },
            tree: {
                usePartRender: true,
                padding: 800,
                dragBy: 'self',
            },
            move: {
                wheel: true,
                drag: false, // 初始状态设置为false，避免自动进入移动模式
                dragButton: 0,
            },
            sky: {
                type: 'draw',
                usePartRender: true,
            },
        });
        
        // 保存引用以便于访问
        appRef.current = app;
        
        const scrollBar = new ScrollBar(app, {
            theme: 'light',
            minSize: 10,
            padding: 30,
            hideDelay: 1000,
        });

        if (app.tree) {
            // 初始设置为false，确保不会自动进入移动模式
            app.tree.draggable = false;
            app.tree.dragBy = 'self';
        }
        
        // 确保鼠标样式为默认状态
        if (app.view) {
            app.view.style.cursor = 'default';
        }
        
        // 将App对象公开到window对象，方便调试
        window.__leaferApp = app;

        stores.editor.setApp(app);
        console.log("编辑器初始化完成，移动状态:", app.config?.move?.drag || false);

        // 显示DOM结构，方便调试
        if (app.view && app.view.canvas) {
            console.log("Canvas元素:", app.view.canvas);
        }
        
        if (app.tree && app.tree.dom) {
            console.log("树DOM结构:", app.tree.dom);
        }

        app.tree.on(ZoomEvent.ZOOM, () => {
            stores.editor.setScale(app.tree.scale);
        });
        app.tree.on(ResizeEvent.RESIZE, () => {
            stores.editor.setScale(app.tree.scale);
        });

        app.editor.on(EditorMoveEvent.SELECT, (event) => {
            const { list } = event;
            if (list.length < 2) return;
            if (list.some(e => e.tag === 'Magnifier')) {
                app.editor.config.rotateable = false;
                app.editor.config.lockRatio = true;
            } else {
                app.editor.config.rotateable = true;
                app.editor.config.lockRatio = false;
            }
        });
        
        let shapeId = null;
        const onStart = (arg) => {
            if (!stores.editor.useTool) return;
            const { target } = arg;
            const shape = stores.editor.getShape(target.id);
            if (shape) return;
            shapeId = nanoid();
            const size = arg.getPageBounds ? arg.getPageBounds() : arg.getPage();
            const type = stores.editor.useTool;
            const newShape = {
                id: shapeId,
                type,
                fill: stores.editor.annotateColor,
                strokeWidth: stores.editor.strokeWidth,
                zIndex: stores.editor.shapes.size + 1,
                ...size
            };
            return newShape;
        }
        app.tree.on(PointerEvent.DOWN, (arg) => {
            const type = stores.editor.useTool;
            if (type !== 'Step') return;
            const newShape = onStart(arg);
            if (!newShape) return;
            newShape.text = stores.editor.nextStep;
            newShape.editable = true;
            stores.editor.addShape(newShape);
            shapeId = null;
            stores.editor.setUseTool(null);
        });
        app.tree.on(DragEvent.START, (arg) => {
            const type = stores.editor.useTool;
            if (type === 'Step') return;
            const newShape = onStart(arg);
            if (!newShape) return;
            if (['Slash', 'MoveDownLeft', 'Pencil', 'Crop'].includes(type)) {
                newShape.points = [newShape.x, newShape.y];
            }
            stores.editor.addShape(newShape);
        });
        app.tree.on(DragEvent.DRAG, (arg) => {
            if (!stores.editor.useTool) return;
            if (!shapeId) return;
            const shape = stores.editor.getShape(shapeId);
            if (!shape) return;
            const size = arg.getPageBounds();
            const max = Math.max(size.width, size.height);
            if (shape.type === 'Magnifier') {
                size.width = max;
                size.height = max;
            }
            const newShape = Object.assign({}, shape, size);
            const { points, type } = newShape;
            if (points && points.length) {
                const { x, y } = arg.getInnerTotal();
                const newX = x > 0 ? size.x + x : size.x;
                const newY = y > 0 ? size.y + y : size.y;
                if (type === 'Pencil') {
                    newShape.points = [...points, newX, newY];
                } else if (type === 'Crop') {
                    const lastX = points[points.length - 2];
                    const lastY = points[points.length - 1];
                    const distance = Math.sqrt(Math.pow(newX - lastX, 2) + Math.pow(newY - lastY, 2));
                    if (distance > 20) {
                        newShape.points = [...points, newX, newY];
                    } else {
                        newShape.points = [...points.slice(0, -2), newX, newY];
                    }
                } else {
                    newShape.points = [points[0], points[1], newX, newY];
                }
            }
            stores.editor.addShape(newShape);
        });
        app.tree.on(DragEvent.END, () => {
            if (!stores.editor.useTool) return;
            if (!shapeId) return;
            const shape = stores.editor.getShape(shapeId);
            if (shape) {
                if ((shape.width === 0 || shape.height === 0) && !['Slash', 'MoveDownLeft', 'Pencil', 'Crop'].includes(shape.type)) {
                    stores.editor.removeShape(shape);
                } else {
                    stores.editor.addShape(Object.assign({}, shape, {editable: true}));
                }
            }
            shapeId = null;
            if (stores.editor.useTool !== 'Pencil') stores.editor.setUseTool(null);
        });
        // 监听容器变化
        const onResize = debounce(() => {
            const { width, height } = target.getBoundingClientRect();
            app.tree.zoom('fit', 100);
            if (stores.option.frameConf.width < width && stores.option.frameConf.height < height) {
                app.tree.zoom(1);
            }
        }, 50);

        addListener(target, onResize);

        // 添加事件处理器以确保可拖动性
        if (target) {
            target.addEventListener('mousedown', (e) => {
                if (stores.editor.move) {
                    console.log("容器鼠标按下事件触发");
                }
            });
        }

        return (() => {
            removeListener(target, onResize);
            if (window.__leaferApp === app) {
                window.__leaferApp = null;
            }
            stores.editor.destroy();
        });
    }, [target]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const { width, height } = target.getBoundingClientRect();
            stores.editor.app.tree.zoom('fit', 100);
            if (stores.option.frameConf.width < width && stores.option.frameConf.height < height) {
                stores.editor.app.tree.zoom(1);
            }
            stores.editor.setScale(stores.editor.app.tree.scale);
        }, 20);
        return (() => {
            clearTimeout(timer);
        })
    }, [stores.option.frameConf.width, stores.option.frameConf.height]);

    if (!stores.editor.app?.tree) return null;
    return (<>
        <FrameBox parent={stores.editor.app?.tree} cursor={stores.editor.cursor} {...stores.option.frameConf}>
            {stores.editor.shapesList.map((item) => {
                const { id, type } = item;
                const props = Object.assign({}, item, type === 'Magnifier' ? {snap: stores.editor.snap} : {});
                return <ShapeLine key={id} {...props} />;
            })}
            {stores.editor.img?.src && <Screenshot />}
            {stores.option.waterImg && <Watermark />}
        </FrameBox>
        <HotKeys />
    </>);
});
